// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import {
  browserSessionPersistence,
  getAuth,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFirestore } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECTID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGEBUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGINGSENDERID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APPID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENTID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);

export const storage = getStorage(app);

let signInPromise: Promise<void> | null = null;

/**
 * Sign this browser session in to Firebase before rules-gated
 * Storage/Firestore access (EHAC-2338).
 *
 * Mints a custom token from the Keycloak session via /api/firebase-token and
 * signs in once; the Firebase SDK's own refresh token keeps the session alive
 * afterwards, so this resolves immediately on subsequent calls. Concurrent
 * callers share one in-flight sign-in.
 *
 * FAIL-OPEN — temporary, only valid while the legacy open rules are live:
 * Firebase is v1's primary document store, so a mint failure (e.g.
 * FIREBASE_TOKEN_MINTER_KEY not yet provisioned → 503) must not block
 * document features while nothing is actually protected. The failure is
 * reported to Sentry and the operation proceeds unauthenticated. Once the
 * phase-2 strict rules are released this flips to fail-closed — tracked as
 * EHAC-2460.
 */
export async function ensureFirebaseAuth(): Promise<void> {
  if (!signInPromise) {
    signInPromise = (async () => {
      try {
        // getAuth throws synchronously (auth/invalid-api-key) when
        // NEXT_PUBLIC_FIREBASE_API_KEY is absent — it must fail open too.
        const auth = getAuth(app);
        if (auth.currentUser) {
          return;
        }
        const response = await fetch('/api/firebase-token');
        if (!response.ok) {
          throw new Error(
            `firebase-token endpoint returned ${response.status}`,
          );
        }
        const { token } = await response.json();
        if (!token) {
          throw new Error('firebase-token endpoint returned no token');
        }
        // Session-scoped persistence: the Firebase session dies with the
        // tab — the backstop for logout paths that never run (crash, tab
        // close) on shared clinical workstations. Explicit cleanup:
        // signOutFirebase().
        await setPersistence(auth, browserSessionPersistence);
        await signInWithCustomToken(auth, token);
      } catch (error) {
        Sentry.captureException(error);
      }
    })().finally(() => {
      signInPromise = null;
    });
  }

  return signInPromise;
}

/**
 * Clear the Firebase session alongside EMR logout (EHAC-2338). Without this,
 * the Firebase refresh token in browser storage outlives the NextAuth
 * session and the next person at a shared workstation inherits document
 * access. Never throws — logout must not be blocked by Firebase cleanup.
 */
export async function signOutFirebase(): Promise<void> {
  try {
    await signOut(getAuth(app));
  } catch {
    // Best-effort: browserSessionPersistence bounds the residual risk.
  }
}
